import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface Study {
  study_id: string;
  quality_score: number;
  study_type: string;
  [key: string]: any;
}

interface Intervention {
  study_id: string;
  mushroom_id: string;
  [key: string]: any;
}

interface Mushroom {
  mushroom_id: string;
  common_name: string;
  [key: string]: any;
}

interface SpeciesEfficacyChartProps {
  studies: Study[];
  interventions: Intervention[];
  mushrooms: Mushroom[];
}

export default function SpeciesEfficacyChart({ studies, interventions, mushrooms }: SpeciesEfficacyChartProps) {
  // Count high-quality RCTs per species
  const speciesData: Record<string, { fiveStar: number; fourStar: number; mushroomId: string }> = {};
  
  studies.forEach(study => {
    if (study.study_type === 'RCT' && study.quality_score >= 4) {
      const intervention = interventions.find(i => i.study_id === study.study_id);
      if (intervention) {
        const mushroom = mushrooms.find(m => m.mushroom_id === intervention.mushroom_id);
        if (mushroom) {
          const name = mushroom.common_name;
          if (!speciesData[name]) {
            speciesData[name] = { fiveStar: 0, fourStar: 0, mushroomId: mushroom.mushroom_id };
          }
          if (study.quality_score === 5) {
            speciesData[name].fiveStar++;
          } else {
            speciesData[name].fourStar++;
          }
        }
      }
    }
  });

  // Prepare chart data - top 10 species
  const chartData = Object.entries(speciesData)
    .map(([name, data]) => ({
      name,
      '5 Stars': data.fiveStar,
      '4 Stars': data.fourStar,
      total: data.fiveStar + data.fourStar
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  if (chartData.length === 0) {
    return (
      <Card className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(10,36,99,0.08)] rounded-xl">
        <CardHeader>
          <CardTitle>High-Quality RCTs by Species</CardTitle>
          <CardDescription>Top species with 4-5 star randomized controlled trials</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-8">No high-quality RCT data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(10,36,99,0.08)] rounded-xl">
      <CardHeader>
        <CardTitle>High-Quality RCTs by Species</CardTitle>
        <CardDescription>Top 10 species with 4-5 star randomized controlled trials</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E1D8" />
            <XAxis 
              type="number"
              stroke="#6E6B64"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              type="category"
              dataKey="name" 
              stroke="#6E6B64"
              style={{ fontSize: '11px' }}
              width={90}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '2px solid #A98A2C',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '12px' }}
            />
            <Bar dataKey="5 Stars" stackId="a" fill="#A98A2C" />
            <Bar dataKey="4 Stars" stackId="a" fill="#C8A53D" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
